<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-6 max-w-[1400px] mx-auto pb-10">

    <!-- Page header -->
    <div class="flex flex-row items-center justify-between gap-4 mb-6 flex-wrap">
      <div class="flex items-center gap-4 min-w-0">
        <button class="h-10 w-10 rounded-full bg-white border border-gray-200 hover:border-amber-300 hover:bg-amber-50 text-gray-500 hover:text-amber-700 flex items-center justify-center shadow-sm transition-colors flex-shrink-0" (click)="onCancel()" matTooltip="Back to attributes" aria-label="Back to attributes">
          <mat-icon class="!text-[20px]">arrow_back</mat-icon>
        </button>
        <div class="min-w-0">
          <div class="text-xs text-gray-400 font-medium">Attributes</div>
          <h1 class="text-2xl font-bold text-gray-900 tracking-tight truncate">{{ isEditMode ? (name || 'Attribute') : 'New attribute' }}</h1>
        </div>
      </div>
      @if (isEditMode) {
      <div class="flex items-center gap-2 flex-wrap">
      <app-record-nav noun="attribute" listName="Attributes" [position]="position" [total]="list.length"
        [prevName]="prev?.attributeName ?? null" [nextName]="next?.attributeName ?? null" [disabled]="loading || saving" (prev)="goTo(prev)" (next)="goTo(next)"></app-record-nav>
      @if (!loading && canDelete) {
      <button class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white border text-sm font-semibold shadow-sm transition-colors"
        [class]="deleteBlockedReason ? 'border-gray-100 text-gray-300 cursor-not-allowed' : 'border-red-100 text-red-600 hover:bg-red-50'"
        (click)="onDelete()" [matTooltip]="deleteBlockedReason || 'Delete attribute'">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">delete</mat-icon> Delete
      </button>
      }
      </div>
      }
    </div>

    @if (loading) {
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm min-h-80 flex flex-col justify-center items-center">
      <mat-spinner diameter="32"></mat-spinner>
      <div class="text-sm text-gray-500 mt-3">Loading attribute&hellip;</div>
    </div>
    } @else {

    <div class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">

      <!-- ============ MAIN COLUMN ============ -->
      <div class="flex flex-col gap-6 min-w-0">

        <!-- Basics -->
        <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div class="flex items-center gap-3 mb-5">
            <span class="h-9 w-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[20px] !h-5 !w-5">tune</mat-icon></span>
            <div><div class="font-bold text-gray-900 leading-tight">Attribute</div><div class="text-xs text-gray-400">What products are described by, e.g. Colour or Finish</div></div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Name</label>
              <input type="text" placeholder="e.g. Colour" class="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm" [class.!border-red-400]="nameError" maxlength="100" [(ngModel)]="name" (ngModelChange)="onNameInput()" />
              @if (nameError) { <div class="text-xs text-red-600 mt-1">{{ nameError }}</div> }
            </div>
            <div>
              <label class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Slug</label>
              <div class="mt-1.5 flex items-stretch gap-2">
                <input type="text" placeholder="pa_colour" class="flex-1 min-w-0 w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm font-mono" [class.!border-red-400]="slugError" maxlength="100" [readonly]="!!slugLockedReason" [class.!bg-gray-100]="!!slugLockedReason" [(ngModel)]="slug" (ngModelChange)="onSlugInput()" />
                <button type="button" class="px-3 rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-amber-300 hover:bg-amber-50 transition-colors disabled:opacity-40" [disabled]="!!slugLockedReason || !name.trim()" (click)="regenerateSlug()" matTooltip="Build the slug from the name">
                  <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">autorenew</mat-icon>
                </button>
              </div>
              @if (slugError) { <div class="text-xs text-red-600 mt-1">{{ slugError }}</div> }
              @if (slugLockedReason) {
              <div class="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500"><mat-icon class="!text-[14px] !h-[14px] !w-[14px]">lock</mat-icon>{{ slugLockedReason }}</div>
              }
            </div>
          </div>
        </div>

        <!-- Values -->
        <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div class="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div class="flex items-center gap-3">
              <span class="h-9 w-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[20px] !h-5 !w-5">list</mat-icon></span>
              <div><div class="font-bold text-gray-900 leading-tight">Values</div><div class="text-xs text-gray-400">{{ values.length }} value{{ values.length === 1 ? '' : 's' }}@if (isEditMode) { &middot; {{ unusedCount }} not used by any product }</div></div>
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              @if (isEditMode && unusedCount > 0 && unusedCount < values.length) {
              <button type="button" class="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl bg-white border border-red-100 text-red-600 hover:bg-red-50 transition-colors" (click)="removeUnused()" matTooltip="Take out every value no product uses (applied when you save)">
                <mat-icon class="!text-[16px] !h-4 !w-4">cleaning_services</mat-icon> Remove {{ unusedCount }} unused
              </button>
              }
              <button type="button" class="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-amber-50 transition-colors" (click)="bulkOpen = !bulkOpen">
                <mat-icon class="!text-[16px] !h-4 !w-4">playlist_add</mat-icon> Add several
              </button>
            </div>
          </div>

          @if (bulkOpen) {
          <div class="mb-4 rounded-xl bg-gray-50 border border-gray-100 p-4">
            <div class="text-xs text-gray-500 mb-2">Paste a list - one value per line, or separated by commas. Values already in the list are skipped.</div>
            <textarea rows="4" placeholder="Beige&#10;Black&#10;Grey" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm !bg-white resize-y" [(ngModel)]="bulkText"></textarea>
            <div class="mt-3 flex justify-end">
              <button type="button" class="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-xl bg-neutral-900 text-white hover:bg-neutral-700 transition-colors disabled:opacity-40" [disabled]="!bulkText.trim()" (click)="addBulk()">
                <mat-icon class="!text-[16px] !h-4 !w-4 !text-amber-400">add</mat-icon> Add to list
              </button>
            </div>
          </div>
          }

          <!-- Add one -->
          <div class="flex items-center gap-2 mb-4">
            <input type="text" placeholder="Add a value and press Enter" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm" maxlength="100" [(ngModel)]="newValueText" (keydown.enter)="addValue()" />
            <button type="button" class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700 transition-colors disabled:opacity-40 flex-shrink-0" [disabled]="!newValueText.trim()" (click)="addValue()">
              <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-400">add</mat-icon> Add
            </button>
          </div>

          @if (values.length > 10) {
          <div class="relative mb-3">
            <mat-icon class="absolute left-3 top-1/2 -translate-y-1/2 !text-gray-400 !text-[18px] !h-[18px] !w-[18px] pointer-events-none">search</mat-icon>
            <input type="text" placeholder="Filter values" class="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm" [(ngModel)]="valueFilter" />
          </div>
          }

          @if (duplicateCount) {
          <div class="mb-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2"><mat-icon class="!text-[16px] !h-4 !w-4">error</mat-icon>{{ duplicateCount }} value{{ duplicateCount === 1 ? ' is' : 's are' }} listed more than once - fix the ones in red before saving.</div>
          }

          @if (values.length === 0) {
          <div class="text-sm text-gray-400 bg-gray-50 rounded-xl px-4 py-6 text-center">No values yet. Add one above, or paste a list with "Add several".</div>
          } @else if (shownValues.length === 0) {
          <div class="text-sm text-gray-400 text-center py-4">No values match "{{ valueFilter }}"</div>
          } @else {
          <div class="rounded-xl border border-gray-100 overflow-hidden">
            <div class="grid grid-cols-[1.4fr_1.2fr_84px_40px] gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
              <div>Value</div><div>Slug</div><div class="text-right">Products</div><div></div>
            </div>
            <div class="max-h-[560px] overflow-y-auto">
              @for (row of shownValues; track row.key) {
              <div class="grid grid-cols-[1.4fr_1.2fr_84px_40px] gap-3 px-4 py-2 items-center border-b border-gray-50 last:border-b-0 hover:bg-amber-50/30 transition-colors">
                <input type="text" maxlength="100" class="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all" [class]="isDuplicate(row) ? '!border-red-400 !bg-red-50' : ''" [(ngModel)]="row.value" (ngModelChange)="onValueInput(row)" placeholder="Value" />
                <input type="text" maxlength="100" class="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all font-mono text-xs !text-gray-500" [(ngModel)]="row.slug" (ngModelChange)="row.slugTouched = true" placeholder="slug" />
                <div class="text-right text-sm">
                  @if (row.id === 0) { <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">New</span> }
                  @else { <span [class]="row.productCount ? 'text-gray-700 font-semibold' : 'text-gray-300'">{{ row.productCount }}</span> }
                </div>
                <div class="flex justify-end">
                  <button type="button" class="h-8 w-8 rounded-full flex items-center justify-center transition-colors"
                    [class]="row.productCount ? 'text-gray-200 cursor-not-allowed' : 'text-gray-300 hover:bg-red-50 hover:text-red-500'"
                    (click)="removeValue(row)" [matTooltip]="row.productCount ? 'Used by ' + row.productCount + ' product(s) - can\'t be removed' : 'Remove value'">
                    <mat-icon class="!text-[18px]">delete</mat-icon>
                  </button>
                </div>
              </div>
              }
            </div>
          </div>
          }
        </div>

      </div>
      <!-- ============ /MAIN COLUMN ============ -->

      <!-- ============ SIDEBAR ============ -->
      <div class="flex flex-col gap-6 xl:sticky xl:top-20">
        @if (isEditMode && detail) {
        <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div class="font-bold text-gray-900 mb-4">About this attribute</div>
          <div class="text-sm">
            <div class="flex items-center justify-between py-2 border-b border-gray-50"><span class="text-gray-400">ID</span><span class="font-mono text-gray-700">{{ detail.attributeId }}</span></div>
            <div class="flex items-center justify-between py-2 border-b border-gray-50"><span class="text-gray-400">Products using it</span><span class="text-gray-700 font-semibold">{{ detail.productCount }}</span></div>
            <div class="flex items-center justify-between py-2 border-b border-gray-50"><span class="text-gray-400">Values saved</span><span class="text-gray-700 font-semibold">{{ detail.values.length }}</span></div>
          </div>
          @if (detail.core) {
          <div class="mt-4 text-xs text-sky-800 bg-sky-50 border border-sky-100 rounded-xl px-3 py-2">Used by the product forms, so its slug is fixed and it can't be deleted.</div>
          }
        </div>
        }

        <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div class="font-bold text-gray-900 mb-3">Good to know</div>
          <ul class="text-xs text-gray-500 flex flex-col gap-2 list-disc pl-4">
            <li>Renaming a value updates it on every product that uses it.</li>
            <li>A value that products use can't be removed - take it off those products first.</li>
            <li>A saved value keeps its slug when renamed, so existing links keep working.</li>
          </ul>
        </div>
      </div>
      <!-- ============ /SIDEBAR ============ -->

    </div>
    }

  </div>

  <!-- Sticky save bar -->
  @if (!loading) {
  <div class="sticky bottom-0 z-20 bg-white/90 backdrop-blur border-t border-gray-200">
    <div class="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between gap-3">
      <span class="text-sm text-gray-400 hidden sm:block">{{ isEditMode ? 'Editing ' + (name || 'attribute') : 'Adding a new attribute' }} &middot; {{ values.length }} value{{ values.length === 1 ? '' : 's' }}</span>
      <div class="flex items-center gap-3 ml-auto">
        <button class="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors" (click)="onCancel()">Cancel</button>
        <button
          class="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          [disabled]="saving" (click)="onSave()">
          @if (saving) { <mat-spinner diameter="16"></mat-spinner> } @else { <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">check</mat-icon> }
          {{ saving ? 'Saving…' : (isEditMode ? 'Save changes' : 'Create attribute') }}
        </button>
      </div>
    </div>
  </div>
  }
  </div>
</app-main-layout>
